import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  checkLimit: (type: 'deck' | 'card' | 'quiz', currentCount?: number) => boolean;
  isUpgradeRequired: (type: 'deck' | 'card' | 'quiz', currentCount?: number) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const status = await subscriptionService.getStatus();
      setSubscription(status);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const checkLimit = (type: 'deck' | 'card' | 'quiz', currentCount: number = 0): boolean => {
    if (!subscription) return false;

    if (type === 'deck') {
      if (subscription.limits.maxDecks === -1) return true; // unlimited
      return subscription.usage.deckCount < subscription.limits.maxDecks;
    }

    if (type === 'card') {
      if (subscription.limits.maxCardsPerDeck === -1) return true; // unlimited
      return currentCount < subscription.limits.maxCardsPerDeck;
    }

    if (type === 'quiz') {
      // Free users: max 3 quizzes, Premium: unlimited
      const QUIZ_LIMIT_FREE = 3;
      if (subscription.subscriptionTier === 'premium' || subscription.isSubscriptionActive) return true; // unlimited
      return currentCount < QUIZ_LIMIT_FREE;
    }

    return false;
  };

  const isUpgradeRequired = (type: 'deck' | 'card' | 'quiz', currentCount: number = 0): boolean => {
    return !checkLimit(type, currentCount) && subscription?.subscriptionTier === 'free';
  };

  const value = {
    subscription,
    loading,
    refreshSubscription,
    checkLimit,
    isUpgradeRequired,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
